<template>
  <vx-card class="w-full">
    <!-- ── 검색 영역 ── -->
    <search-tools v-model="search" useReset @search="loadData" @resetSearch="onClickReset">
      <template #default="{ setRef }">
        <search-select-field label="사업장명" :setRef="setRef" v-model="search.field" :options="{ nav: search }" @input="onChangeScope" />
        <search-year label="연도" v-model="search.year" :options="{ default: new Date().getFullYear(), afterFix: '년' }" @input="onChangeScope" />
        <search-month label="월" v-model="search.month" :options="{ default: new Date().getMonth() + 1 }" @input="onSelectChange" />
        <search-keyword label="이름" placeholder="이름" v-model="search.이름" :options="{}" :onClickSearch="loadData" />
        <search-keyword label="부서명" placeholder="부서명" v-model="search.부서명" :options="{}" :onClickSearch="loadData" />
        <search-select label="직무" v-model="search.직무" :query="{ s_jm: '' }" :options="{ items: 직무Options }" @input="onSelectChange" />
        <search-select label="직급" v-model="search.직급" :query="{ s_jg: '' }" :options="{ items: 직급Options }" @input="onSelectChange" />
        <search-keyword label="기타검색" placeholder="Search" v-model="search.keyword" :options="{}" :onClickSearch="loadData" />
      </template>
    </search-tools>

    <!-- ── perPage + 버튼 영역 ── -->
    <div class="flex items-center justify-between mb-4">
      <vs-select v-model="perPage" @change="onChangePerPage" style="width: 130px">
        <vs-select-item v-for="n in [20, 50, 100]" :key="n" :value="n" :text="`${n}명씩 보기`" />
      </vs-select>
      <div class="flex gap-2">
        <vs-button color="secondary" type="border" @click="onClickExcel">
          <div class="flex items-center gap-1">
            <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4" />
            Excel 다운로드
          </div>
        </vs-button>
        <vs-button color="secondary" type="border" @click="onClickPrint">
          <div class="flex items-center gap-1">
            <feather-icon icon="PrinterIcon" svgClasses="h-4 w-4" />
            인쇄
          </div>
        </vs-button>
      </div>
    </div>

    <!-- ── 로딩 ── -->
    <div v-if="isLoading" class="py-10 text-center">
      <vs-progress indeterminate color="primary" />
      <p class="mt-2 text-gray-500">데이터를 불러오는 중...</p>
    </div>

    <!-- ── 모니터링 테이블 ── -->
    <div v-else ref="scrollContainer" class="overflow-x-auto" :class="{ 'is-dragging': isDragging }" @mousedown="onDragStart" @mousemove="onDragMove" @mouseup="onDragEnd" @mouseleave="onDragEnd">
      <table class="monitor-table" id="monitor-table">
        <colgroup>
          <col style="width: 50px" />
          <col style="width: 90px" />
          <col style="width: 90px" />
          <col style="width: 70px" />
          <col style="width: 60px" />
          <col style="width: 90px" />
          <col v-for="d in days" :key="`col-${d}`" style="width: 34px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2" class="sticky-col sticky-col-0">No.</th>
            <th rowspan="2" class="sticky-col sticky-col-1">사업장명</th>
            <th rowspan="2" class="sticky-col sticky-col-2">부서명</th>
            <th rowspan="2" class="sticky-col sticky-col-3">이름</th>
            <th rowspan="2" class="sticky-col sticky-col-4">직급</th>
            <th rowspan="2" class="sticky-col sticky-col-5">직무</th>
            <th :colspan="days.length" class="month-header">{{ appliedMonth }}월</th>
          </tr>
          <tr>
            <th v-for="d in days" :key="`th-${d}`" class="day-th" :class="getDayClass(d)">{{ d }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredSupervisors.length === 0">
            <td :colspan="6 + days.length" class="py-10 text-center text-gray-400">데이터가 없습니다.</td>
          </tr>
          <tr v-for="(sup, i) in filteredSupervisors" :key="sup.postId">
            <td class="sticky-col sticky-col-0">{{ (page - 1) * perPage + i + 1 }}</td>
            <td class="sticky-col sticky-col-1">{{ sup.사업장명 }}</td>
            <td class="sticky-col sticky-col-2">{{ sup.부서명 }}</td>
            <td class="sticky-col sticky-col-3">{{ sup.이름 }}</td>
            <td class="sticky-col sticky-col-4">{{ sup.직급 }}</td>
            <td class="sticky-col sticky-col-5">{{ sup.직무 }}</td>
            <td v-for="d in days" :key="`cell-${sup.postId}-${d}`" class="day-cell" :class="getCellClass(sup, d)" @click="onClickCell(sup, d)">
              <template v-if="getCellDocs(sup, d).length === 0">
                <span class="dot-empty">●</span>
              </template>
              <template v-else>
                <span class="doc-count">{{ getCellDocs(sup, d).length }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── 페이징 ── -->
    <div v-if="!isLoading" class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage ? lastPage : 1" :value="page" @change="onChangePage" />
      </div>
    </div>

    <!-- ── 신규 작성 확인 팝업 ── -->
    <vs-popup title="신규 문서 작성" :active.sync="showNewDocPopup">
      <div v-if="newDocTarget" style="padding: 8px 0">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px">
          <tbody>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; width: 80px; text-align: left">사업장</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.사업장명 }}</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">부서명</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.부서명 }}</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">이름</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.이름 }} ({{ newDocTarget.sup.직급 }})</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">직무</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.직무 }}</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">점검일</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ appliedYear }}년 {{ appliedMonth }}월 {{ newDocTarget.day }}일</td>
            </tr>
          </tbody>
        </table>
        <p style="color: #555; font-size: 13px; margin-bottom: 16px">위 정보로 새 문서를 작성하시겠습니까?<br />작성 페이지는 새 탭에서 열립니다.</p>
        <div style="display: flex; justify-content: flex-end; gap: 8px">
          <vs-button
            type="border"
            color="dark"
            @click="
              showNewDocPopup = false;
              newDocTarget = null;
            "
            >취소</vs-button
          >
          <vs-button color="primary" @click="openNewDoc">새 탭에서 작성</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- ── 문서 목록 팝업 ── -->
    <vs-popup title="작성 문서 목록" :active.sync="showDocListPopup">
      <table class="w-full" style="border-collapse: collapse">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">No.</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">점검일</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">점검시간</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">열람</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(doc, di) in selectedDocs" :key="doc.postId">
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ di + 1 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ doc.점검일 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ doc.점검시간 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">
              <vs-button size="small" color="primary" type="border" @click="openDoc(doc)">열람</vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vs-popup>
  </vx-card>
</template>

<script>
import moment from "moment";
import ExcelJS from "exceljs";
import excelSupporter from "@/excelSupporter";
import SearchTools from "@/views/list/components/SearchTools.vue";
import KPagination from "@/components/kPagination/kPagination.vue";
import SearchSelectField from "@/views/list/components/SearchSelectField.vue";
import SearchSelect from "@/views/list/components/SearchSelect.vue";
import SearchYear from "@/views/list/components/SearchYear.vue";
import SearchMonth from "@/views/list/components/SearchMonth.vue";
import SearchKeyword from "@/views/list/components/SearchKeyword.vue";

export default {
  name: "Page1141",
  components: {
    SearchTools,
    KPagination,
    SearchSelectField,
    SearchSelect,
    SearchYear,
    SearchMonth,
    SearchKeyword,
  },
  props: {
    board: Object,
    permission: {
      type: Object,
      default: () => ({ c: 0, r: 0, u: 0, d: 0, v: 0 }),
    },
  },
  data() {
    return {
      search: {
        field: null,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        이름: "",
        부서명: "",
        직무: "",
        직급: "",
        keyword: "",
      },

      // 직무/직급은 738 에 자유 입력으로 저장되므로 실제 등록된 값을 수집해 셀렉트 목록으로 사용
      직무Options: [{ text: "전체", value: "" }],
      직급Options: [{ text: "전체", value: "" }],

      appliedYear: new Date().getFullYear(),
      appliedMonth: new Date().getMonth() + 1,

      supervisors: [], // board 738 데이터
      inspectDocs: [], // board 1140 데이터

      isLoading: false,
      showDocListPopup: false,
      selectedDocs: [],
      showNewDocPopup: false,
      newDocTarget: null,

      page: 1,
      lastPage: 1,
      perPage: 50,

      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,

      autoSearchReady: false,
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    daysInMonth() {
      return new Date(this.appliedYear, this.appliedMonth, 0).getDate();
    },
    days() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1);
    },
    filteredSupervisors() {
      return this.supervisors;
    },
    // postId -> { day: [docs] }
    docMap() {
      return this.buildDocMap(this.inspectDocs);
    },
  },
  created() {
    this.loadFilterOptions();
    this.loadData();
    this.$nextTick(() => {
      this.autoSearchReady = true;
    });
  },
  methods: {
    onSelectChange() {
      if (this.autoSearchReady) this.loadData();
    },

    // ─── 검색조건 초기화 ─────────────────────────────────────────
    async onClickReset() {
      // field 는 SearchSelectField 의 watch 에서 구조분해 되므로 null 이 아닌 빈 객체로 초기화한다
      this.search.field = { fieldId: "", fieldName: "" };
      this.search.year = new Date().getFullYear();
      this.search.month = new Date().getMonth() + 1;
      this.search.이름 = "";
      this.search.부서명 = "";
      this.search.직무 = "";
      this.search.직급 = "";
      this.search.keyword = "";

      await this.loadFilterOptions();
      this.loadData();
    },

    // 연도/사업장이 바뀌면 직무·직급 목록의 모집단 자체가 달라지므로 옵션을 다시 구성한다
    async onChangeScope() {
      if (!this.autoSearchReady) return;
      await this.loadFilterOptions();
      this.loadData();
    },

    // ─── 직무/직급 셀렉트 목록 구성 ──────────────────────────────
    async loadFilterOptions() {
      const year = this.search.year || new Date().getFullYear();
      const conditions = [
        { key: "header.year", operator: "=", value: year },
        { key: "body.관리감독자정보.구분", operator: "!=", value: "관리직" },
      ];
      if (this.search.field && this.search.field.fieldId) {
        conditions.push({ key: "body.stdInfo.field_id", operator: "=", value: this.search.field.fieldId });
      }

      const 직무Set = new Set();
      const 직급Set = new Set();
      try {
        let page = 1;
        for (;;) {
          const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: this.projectId,
            boardId: 738,
            boardSlug: "board-738",
            page,
            perPage: 1000,
            orderTarget: "created_at",
            orderDirection: "asc",
            conditions,
            withMeta: true,
            withoutRelation: true,
            onlyMeta: true,
            metaKeys: ["body.관리감독자정보.직무", "body.관리감독자정보.직급"],
          });
          const data = res.data || [];
          data.forEach((item) => {
            const content = item.postContents ? item.postContents : JSON.parse(item.post_content);
            const info = (content.body && content.body.관리감독자정보) || {};
            if (info.직무) 직무Set.add(String(info.직무).trim());
            if (info.직급) 직급Set.add(String(info.직급).trim());
          });
          if (page >= (res.last_page || 1)) break;
          page++;
        }
      } catch (e) {
        console.error("직무/직급 목록 조회 오류:", e);
      }

      const toOptions = (set) => [{ text: "전체", value: "" }, ...[...set].filter(Boolean).sort((a, b) => a.localeCompare(b, "ko")).map((v) => ({ text: v, value: v }))];
      this.직무Options = toOptions(직무Set);
      this.직급Options = toOptions(직급Set);

      // 새 목록에 없는 선택값은 초기화
      if (this.search.직무 && !직무Set.has(this.search.직무)) this.search.직무 = "";
      if (this.search.직급 && !직급Set.has(this.search.직급)) this.search.직급 = "";
    },

    // ─── 738 조회 조건 (목록/엑셀/인쇄 공통) ─────────────────────
    buildSupervisorConditions() {
      const conditions = [
        { key: "header.year", operator: "=", value: this.appliedYear },
        { key: "body.관리감독자정보.구분", operator: "!=", value: "관리직" },
      ];
      if (this.search.field && this.search.field.fieldId) {
        conditions.push({ key: "body.stdInfo.field_id", operator: "=", value: this.search.field.fieldId });
      }
      if (this.search.이름) {
        conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.이름}%` });
      }
      if (this.search.부서명) {
        conditions.push({ key: "body.관리감독자정보.부서명", operator: "LIKE", value: `%${this.search.부서명}%` });
      }
      if (this.search.직무) {
        conditions.push({ key: "body.관리감독자정보.직무", operator: "=", value: this.search.직무 });
      }
      if (this.search.직급) {
        conditions.push({ key: "body.관리감독자정보.직급", operator: "=", value: this.search.직급 });
      }
      // keyword 는 OR 그룹이므로 반드시 AND 조건들보다 뒤에 붙인다
      if (this.search.keyword) {
        conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.stdInfo.field_name", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.관리감독자정보.직무", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.관리감독자정보.부서명", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
      }
      return conditions;
    },

    // ─── 데이터 로딩 ────────────────────────────────────────────
    async loadData() {
      if (!this.search.month) {
        this.$vs.notify({ title: "알림", text: "월을 선택해주세요.", color: "warning" });
        return;
      }
      this.appliedYear = this.search.year || new Date().getFullYear();
      this.appliedMonth = this.search.month;
      this.page = 1;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },

    async loadSupervisors() {
      const conditions = this.buildSupervisorConditions();
      const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: 738,
        boardSlug: "board-738",
        page: this.page,
        perPage: this.perPage,
        orderTarget: "created_at",
        orderDirection: "asc",
        conditions,
      });
      this.lastPage = res.last_page || 1;
      this.supervisors = (res.data || []).map((item) => {
        const body = JSON.parse(item.post_content).body;
        const info = body.관리감독자정보 || {};
        return {
          postId: item.id,
          boardId: item.board_id,
          사번: `${info.사번 || ""}`.trim(),
          사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
          이름: info.이름 || "",
          직무: info.직무 || "",
          직급: info.직급 || "",
          부서명: info.부서명 || "",
          raw: item,
        };
      });
    },

    // 조회한 달의 점검문서 조회 조건 묶음을 만든다.
    // 1140 은 작성 당시 738 게시글의 id 를 body.관리감독자정보.postId 에 박아두는데,
    // 738 은 연도별로 게시글이 따로 등록되므로 같은 사람이라도 id 가 여러 개다.
    // 그래서 id 만으로 맞추면 작성 시 다른 연도 명부가 잡힌 문서가 통째로 누락된다.
    // 연도가 달라도 변하지 않는 사번을 기준으로 맞추고,
    // 사번이 등록되지 않은 감독자만 기존대로 id 로 맞춘다.
    buildDocConditionSets(supervisors) {
      const startDate = `${this.appliedYear}-${String(this.appliedMonth).padStart(2, "0")}-01`;
      const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");
      const base = [
        { key: "body.점검일", operator: ">=", value: startDate },
        { key: "body.점검일", operator: "<=", value: endDate },
      ];

      // 대상을 주지 않으면(엑셀/인쇄) 그 달 전체를 받는다.
      // 전체 감독자의 사번을 IN 조건으로 넘기면 쿼리스트링이 50명당 약 3KB 로 늘어
      // URL 길이 제한에 걸리는데, 한 달치 전체라도 요청 몇 번이면 끝난다.
      if (!supervisors) return [base];

      const 사번s = [...new Set(supervisors.map((s) => s.사번).filter(Boolean))];
      const postIds = supervisors.filter((s) => !s.사번).map((s) => s.postId);

      const sets = [];
      if (사번s.length > 0) sets.push([...base, { key: "body.관리감독자정보.사번", operator: "IN", value: 사번s }]);
      if (postIds.length > 0) sets.push([...base, { key: "body.관리감독자정보.postId", operator: "IN", value: postIds }]);
      return sets;
    },

    async fetchInspectDocs(supervisors) {
      const allDocs = [];
      const seen = new Set();

      for (const conditions of this.buildDocConditionSets(supervisors)) {
        let page = 1;
        for (;;) {
          const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: this.projectId,
            boardId: 1140,
            boardSlug: "board-1140",
            page,
            perPage: 1000,
            orderTarget: "created_at",
            orderDirection: "asc",
            conditions,
            withMeta: true,
            withoutRelation: true,
            onlyMeta: true,
            metaKeys: ["body.관리감독자정보.postId", "body.관리감독자정보.사번", "body.점검일", "body.점검시간", "body.작성일"],
          });
          const data = res.data || [];
          data.forEach((item) => {
            // 사번 조건과 id 조건에 함께 걸린 문서는 한 번만 담는다
            if (seen.has(item.id)) return;
            seen.add(item.id);
            const content = item.postContents ? item.postContents : JSON.parse(item.post_content);
            const body = content.body || {};
            const info = body.관리감독자정보 || {};
            allDocs.push({
              postId: item.id,
              boardId: item.board_id,
              사번: `${info.사번 || ""}`.trim(),
              connect738PostId: info.postId || null,
              점검일: body.점검일 || "",
              점검시간: body.점검시간 || "",
              작성일: body.작성일 || "",
            });
          });
          if (page >= (res.last_page || 1)) break;
          page++;
        }
      }
      return allDocs;
    },

    async loadInspectDocs() {
      if (this.supervisors.length === 0) {
        this.inspectDocs = [];
        return;
      }
      this.inspectDocs = await this.fetchInspectDocs(this.supervisors);
    },

    // ─── 셀 헬퍼 ─────────────────────────────────────────────────
    // "사번:xxx" / "postId:xxx" -> { day: [docs] }
    // 사번과 id 두 가지로 찾을 수 있도록 같은 문서를 양쪽 키에 넣어둔다.
    buildDocMap(docs) {
      const map = {};
      const put = (key, day, doc) => {
        if (!map[key]) map[key] = {};
        if (!map[key][day]) map[key][day] = [];
        map[key][day].push(doc);
      };
      for (const doc of docs) {
        const day = parseInt(`${doc.점검일}`.split("-")[2]);
        if (!day) continue;
        if (doc.사번) put(`사번:${doc.사번}`, day, doc);
        if (doc.connect738PostId) put(`postId:${doc.connect738PostId}`, day, doc);
      }
      return map;
    },

    getCellDocsFrom(map, sup, day) {
      const pick = (key) => (map[key] || {})[day] || [];
      const bySabun = sup.사번 ? pick(`사번:${sup.사번}`) : [];
      const byPostId = pick(`postId:${sup.postId}`);
      if (bySabun.length === 0) return byPostId;
      if (byPostId.length === 0) return bySabun;
      // 사번이 바뀐 이력 등으로 양쪽에 따로 걸린 문서가 있을 수 있어 합쳐서 중복만 제거한다
      const ids = new Set(bySabun.map((d) => d.postId));
      return [...bySabun, ...byPostId.filter((d) => !ids.has(d.postId))];
    },

    getCellDocs(sup, day) {
      return this.getCellDocsFrom(this.docMap, sup, day);
    },

    getCellClass(sup, day) {
      const count = this.getCellDocs(sup, day).length;
      if (count === 0) return "cell-empty";
      return "cell-written";
    },

    getDayClass(day) {
      const date = new Date(this.appliedYear, this.appliedMonth - 1, day);
      const dow = date.getDay();
      if (dow === 0) return "day-sun";
      if (dow === 6) return "day-sat";
      return "";
    },

    // ─── 셀 클릭 ─────────────────────────────────────────────────
    onClickCell(sup, day) {
      const docs = this.getCellDocs(sup, day);

      if (docs.length === 0) {
        this.newDocTarget = { sup, day };
        this.showNewDocPopup = true;
      } else {
        this.selectedDocs = docs;
        this.showDocListPopup = true;
      }
    },

    openNewDoc() {
      if (!this.newDocTarget) return;
      const { sup, day } = this.newDocTarget;
      const 점검일 = `${this.appliedYear}-${String(this.appliedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const base = `${window.location.origin}/project/${this.projectId}/tab/1140/write`;
      const query = `?connect738PostId=${sup.postId}&점검일=${점검일}`;
      const win = window.open(base + query, "_blank", `width=${screen.width},height=${screen.height}`);
      if (win) win.opener = null;
      this.showNewDocPopup = false;
      this.newDocTarget = null;
    },

    openDoc(doc) {
      const url = `${window.location.origin}/project/${this.projectId}/tab/1140/write/${doc.postId}`;
      const win = window.open(url, "_blank", `width=${screen.width},height=${screen.height}`);
      if (win) win.opener = null;
      this.showDocListPopup = false;
    },

    // ─── 페이징 ──────────────────────────────────────────────────
    async onChangePage(p) {
      if (this.page === p) return;
      this.page = p;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },
    async onChangePerPage() {
      if (this.isLoading) return;
      this.page = 1;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },

    // ─── 드래그 스크롤 ───────────────────────────────────────────
    onDragStart(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.dragStartX = e.pageX - this.$refs.scrollContainer.offsetLeft;
      this.dragScrollLeft = this.$refs.scrollContainer.scrollLeft;
    },
    onDragMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - this.$refs.scrollContainer.offsetLeft;
      const walk = (x - this.dragStartX) * 1.2;
      this.$refs.scrollContainer.scrollLeft = this.dragScrollLeft - walk;
    },
    onDragEnd() {
      this.isDragging = false;
    },

    // ─── Excel 다운로드 ───────────────────────────────────────────
    async loadAllSupervisors() {
      const allSupervisors = [];
      let page = 1;
      while (true) {
        const conditions = this.buildSupervisorConditions();
        const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 738,
          boardSlug: "board-738",
          page,
          perPage: 100,
          orderTarget: "created_at",
          orderDirection: "asc",
          conditions,
        });
        const data = res.data || [];
        data.forEach((item) => {
          const body = JSON.parse(item.post_content).body;
          const info = body.관리감독자정보 || {};
          const sup = {
            postId: item.id,
            사번: `${info.사번 || ""}`.trim(),
            사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
            이름: info.이름 || "",
            직무: info.직무 || "",
            직급: info.직급 || "",
            부서명: info.부서명 || "",
          };
          allSupervisors.push(sup);
        });
        if (page >= (res.last_page || 1)) break;
        page++;
      }
      return allSupervisors;
    },

    async onClickExcel() {
      this.loading(true);
      const allSupervisors = await this.loadAllSupervisors();
      // 화면의 docMap 은 현재 페이지 감독자분만 담고 있다. 출력 대상은 전체 감독자이므로
      // 그 달 전체 점검문서로 별도 맵을 만든다.
      const docMap = this.buildDocMap(await this.fetchInspectDocs());
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`${this.appliedMonth}월`);

      // 컬럼 너비 설정
      const colLetter = (n) => {
        let r = "";
        while (n > 0) {
          n--;
          r = String.fromCharCode(65 + (n % 26)) + r;
          n = Math.floor(n / 26);
        }
        return r;
      };
      worksheet.getColumn("A").width = 6;
      worksheet.getColumn("B").width = 14;
      worksheet.getColumn("C").width = 14;
      worksheet.getColumn("D").width = 10;
      worksheet.getColumn("E").width = 10;
      worksheet.getColumn("F").width = 14;
      for (let i = 0; i < this.days.length; i++) {
        worksheet.getColumn(colLetter(7 + i)).width = 5;
      }

      const lastDayCol = colLetter(6 + this.days.length);

      // ── 헤더 1행: 빈칸 + 월 ──
      excelSupporter.addHeader(worksheet, [
        { value: "", style: "borderHead", merge: "A:A" },
        { value: "", style: "borderHead", merge: "B:B" },
        { value: "", style: "borderHead", merge: "C:C" },
        { value: "", style: "borderHead", merge: "D:D" },
        { value: "", style: "borderHead", merge: "E:E" },
        { value: "", style: "borderHead", merge: "F:F" },
        { value: `${this.appliedMonth}월`, style: "borderHead", merge: `G:${lastDayCol}` },
      ]);

      // ── 헤더 2행: 고정컬럼명 + 날짜 ──
      excelSupporter.addHeader(worksheet, [
        { value: "No.", style: "borderHead", merge: "A:A" },
        { value: "사업장명", style: "borderHead", merge: "B:B" },
        { value: "부서명", style: "borderHead", merge: "C:C" },
        { value: "이름", style: "borderHead", merge: "D:D" },
        { value: "직급", style: "borderHead", merge: "E:E" },
        { value: "직무", style: "borderHead", merge: "F:F" },
        ...this.days.map((d, i) => ({
          value: d,
          style: "borderHead",
          merge: `${colLetter(7 + i)}:${colLetter(7 + i)}`,
        })),
      ]);

      // ── colgroup ──
      const colgroup = ["A:A", "B:B", "C:C", "D:D", "E:E", "F:F", ...this.days.map((_, i) => `${colLetter(7 + i)}:${colLetter(7 + i)}`)];

      // ── 데이터 행 ──
      const tableData = allSupervisors.map((sup, idx) => [
        String(idx + 1),
        sup.사업장명,
        sup.부서명,
        sup.이름,
        sup.직급,
        sup.직무,
        ...this.days.map((d) => {
          const count = this.getCellDocsFrom(docMap, sup, d).length;
          return count === 0 ? "●" : String(count);
        }),
      ]);

      excelSupporter.addTable(worksheet, {
        rowHeight: 18,
        colgroup,
        data: tableData,
      });

      // ── 다운로드 ──
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `관리감독자_점검실적_${this.appliedYear}_${this.appliedMonth}월.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      this.loading(false);
    },

    loading(open) {
      window.dispatchEvent(new CustomEvent("loadingDialog", { detail: { open } }));
    },

    // ─── 인쇄 ─────────────────────────────────────────────────────
    async onClickPrint() {
      this.loading(true);
      const allSupervisors = await this.loadAllSupervisors();
      // 엑셀과 같은 이유로 전체 감독자 기준의 맵을 새로 만든다.
      const docMap = this.buildDocMap(await this.fetchInspectDocs());
      this.loading(false);

      const filterInfo = [
        this.search.field && this.search.field.fieldName ? this.search.field.fieldName : "",
        `${this.appliedYear}년 ${this.appliedMonth}월`,
        this.search.부서명 || "",
        this.search.이름 || "",
        this.search.직급 || "",
        this.search.직무 || "",
      ]
        .filter(Boolean)
        .join(", ");

      const colLetterFn = (n) => {
        let r = "";
        while (n > 0) {
          n--;
          r = String.fromCharCode(65 + (n % 26)) + r;
          n = Math.floor(n / 26);
        }
        return r;
      };

      const thDays = this.days
        .map((d) => {
          const date = new Date(this.appliedYear, this.appliedMonth - 1, d);
          const dow = date.getDay();
          const color = dow === 0 ? "#e53e3e" : dow === 6 ? "#3182ce" : "#3c3c3c";
          return `<th style="color:${color}; width:22px;">${d}</th>`;
        })
        .join("");

      const rows = allSupervisors
        .map((sup, idx) => {
          const cells = this.days
            .map((d) => {
              const count = this.getCellDocsFrom(docMap, sup, d).length;
              const text = count === 0 ? "●" : String(count);
              const color = count === 0 ? "#e53e3e" : "#200c63";
              return `<td style="color:${color};">${text}</td>`;
            })
            .join("");
          return `<tr>
          <td>${idx + 1}</td>
          <td>${sup.사업장명}</td>
          <td>${sup.부서명}</td>
          <td>${sup.이름}</td>
          <td>${sup.직급}</td>
          <td>${sup.직무}</td>
          ${cells}
        </tr>`;
        })
        .join("");

      const logoUrl = `${window.location.origin}/img/동국제강_로그인.svg`;

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>관리감독자 점검실적 모니터링</title>
  <style>
    @page { size: A3 landscape; margin: 10mm; }
    body { font-family: "Noto Sans KR", sans-serif; font-size: 9px; color: #1a1a1a; margin: 0; }
    .page-header { display: flex; align-items: center; position: relative; margin-bottom: 16px; }
    .logo { height: 28px; }
    .page-title { position: absolute; left: 50%; transform: translateX(-50%); font-size: 18px; font-weight: 700; white-space: nowrap; }
    .filter-info { margin-left: auto; font-size: 10px; color: #555; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8px; }
    th, td { border: 1px solid #ccc; padding: 3px 2px; text-align: center; vertical-align: middle; overflow: hidden; white-space: nowrap; }
    th { background: #f0f0f0; font-weight: 600; }
    .month-th { background: #e8e8f0; font-size: 10px; font-weight: 700; }
    col.fixed { width: 50px; }
    col.day { width: 22px; }
  </style>
</head>
<body>
  <div class="page-header">
    <img src="${logoUrl}" class="logo" onerror="this.style.display='none'" />
    <span class="page-title">관리감독자 점검실적 모니터링</span>
    <span class="filter-info">${filterInfo}</span>
  </div>
  <table>
    <colgroup>
      <col style="width:32px"/><col style="width:56px"/><col style="width:56px"/><col style="width:48px"/><col style="width:42px"/><col style="width:56px"/>
      ${this.days.map(() => '<col style="width:22px"/>').join("")}
    </colgroup>
    <thead>
      <tr>
        <th rowspan="2">No.</th>
        <th rowspan="2">사업장명</th>
        <th rowspan="2">부서명</th>
        <th rowspan="2">이름</th>
        <th rowspan="2">직급</th>
        <th rowspan="2">직무</th>
        <th colspan="${this.days.length}" class="month-th">${this.appliedMonth}월</th>
      </tr>
      <tr>${thDays}</tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

      const win = window.open("", "_blank", `width=${screen.width},height=${screen.height}`);
      win.document.write(html);
      win.document.close();
      win.onload = () => {
        win.print();
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  cursor: grab;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }
}

.label-text {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.monitor-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    padding: 6px 4px;
    text-align: center;
    vertical-align: middle;
  }

  thead th {
    border-top: 1px solid #e0e0e0;
  }

  td:first-child,
  th:first-child {
    border-left: 1px solid #e0e0e0;
  }

  th {
    background: #f5f5f5;
    font-weight: 500;
    white-space: nowrap;
  }

  /* 고정 컬럼 sticky */
  .sticky-col {
    position: sticky;
    z-index: 2;
    background: #f5f5f5;
  }

  td.sticky-col {
    background: #fff;
  }

  .sticky-col-0 {
    left: 0;
    width: 50px;
    min-width: 50px;
  }
  .sticky-col-1 {
    left: 50px;
    width: 90px;
    min-width: 90px;
  }
  .sticky-col-2 {
    left: 140px;
    width: 90px;
    min-width: 90px;
  }
  .sticky-col-3 {
    left: 230px;
    width: 70px;
    min-width: 70px;
  }
  .sticky-col-4 {
    left: 300px;
    width: 60px;
    min-width: 60px;
  }
  .sticky-col-5 {
    left: 360px;
    width: 90px;
    min-width: 90px;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.08);
  }

  .month-header {
    background: #ededf5;
    font-size: 13px;
    font-weight: 600;
  }

  .day-th {
    padding: 6px 2px;
    font-size: 11px;
  }

  .day-sun {
    color: #e53e3e;
    background: #fff5f5;
  }
  .day-sat {
    color: #3182ce;
    background: #ebf8ff;
  }

  .day-cell {
    cursor: pointer;
    padding: 4px 2px;
    font-size: 12px;
    font-weight: 500;
    transition: background 0.15s;

    &:hover {
      background: #f0f0f0;
    }

    &.cell-empty {
      .dot-empty {
        color: #e53e3e;
        font-size: 10px;
      }
    }

    &.cell-written {
      .doc-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #38a169;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
      }
    }
  }
}
</style>
